import React, { forwardRef, useMemo } from "react";
import { useField, useFormikContext } from "formik";
import { FileUpload } from "./FileUpload";
import type { FileUploadProps, FileUploadRef } from "./types/FileUpload.types";
import type { UploadedFile } from "./types/UploadedFile.type";
import type { UploadFile } from "types/storage.types";

interface FormikFileUploadProps
	extends Omit<FileUploadProps, "onChange" | "initialValue"> {
	name: string;
}

/**
 * Formik-aware wrapper for FileUpload.
 *
 * Keeps `values[name]` in sync with the uploaded files list by mapping
 * the internal `UploadedFile` objects to the backend-expected shape:
 * `{ id, file_name, file_path, file_type, file_size }`.
 */
export const FormikFileUpload = forwardRef<FileUploadRef, FormikFileUploadProps>(
	({ name, ...props }, ref) => {
		const [field] = useField(name);
		const { setFieldValue } = useFormikContext();

		const handleChange = (files: UploadedFile[]) => {
			const mapped = files
				.filter((f) => f.id && (f.path || f.storage_path))
				.map((f) => ({
					id: f.id,
					file_name: f.name,
					file_path: f.path,
					file_type: f.type ?? null,
					file_size: f.size ?? 0,
					storage_path: f.storage_path ?? f.path,
				}));

			setFieldValue(name, mapped, false);
		};

		// Memoize by the serialised field value so we don't create a new
		// array reference on every render (which would reset FileUpload state).
		const fieldJson = JSON.stringify(field.value);
		const initialValue: UploadFile[] | undefined = useMemo(() => {
			const raw = JSON.parse(fieldJson) as unknown;
			if (!raw) {
				return undefined;
			}
			const items = Array.isArray(raw) ? raw : [raw];
			return items
				.filter((f: Record<string, unknown>) => f && f.id)
				.map((f: Record<string, unknown>) => ({
					id: f.id as number,
					file_name: f.file_name as string,
					file_path: f.file_path as string,
					file_size: (f.file_size as number) ?? 0,
					file_type: f.file_type as string | undefined,
				}));
		}, [fieldJson]);

		return (
			<FileUpload
				ref={ref}
				onChange={handleChange}
				initialValue={initialValue}
				{...props}
			/>
		);
	}
);

FormikFileUpload.displayName = "FormikFileUpload";
